import { Table } from '@/components_deprecated'
import type { DynamicColumnNode } from '@/modules/dynamic_tables'
import { format } from '@/utils/date-fns'
import { parseISO } from 'date-fns'
import React, { useMemo, useState } from 'react'
import { isMobile } from 'react-device-detect'
import { useNavigate } from 'react-router'
import {
  ActivitiesWeekTableRow,
  ActivitiesWeekTableSubRow,
} from './ActivitiesWeekTableRow'
import { ActivitiesWeekTableSummation } from './ActivitiesWeekTableSummation'
import type { WeekReport } from '../../types.graphql'

interface ActivitiesWeekTableProps {
  week: WeekReport
  dateStart: Date
  dateEnd: Date
  userId: string
  detailedDate: boolean
  columns?: DynamicColumnNode[]
}

export const ActivitiesWeekTable: React.FC<ActivitiesWeekTableProps> = ({
  week,
  dateStart,
  dateEnd,
  userId,
  detailedDate,
  columns = [],
}) => {
  const tableHeaders = useMemo(
    () => (
      <tr>
        {columns.map(column => (
          <th key={column.name}>{column.displayName || column.name}</th>
        ))}
      </tr>
    ),
    [columns]
  )

  const activityRows = useMemo(() => {
    return week.dayReports.map(dayReport => (
      <ActivitiesWeekTableRowWrapper
        key={dayReport.date}
        dayReport={dayReport}
        userId={userId}
        columns={columns}
        dateStart={dateStart}
        dateEnd={dateEnd}
        detailedDate={detailedDate}
      />
    ))
  }, [week.dayReports, userId, columns, dateStart, dateEnd, detailedDate])

  return (
    <Table headerMargin rowHoverBackground="gray10">
      <thead>{tableHeaders}</thead>

      <tbody style={{ position: 'relative' }}>
        {activityRows}
        <ActivitiesWeekTableSummation week={week} columns={columns} />
      </tbody>
    </Table>
  )
}

interface ActivitiesWeekTableRowWrapperProps {
  dayReport: WeekReport['dayReports'][0]
  userId: string
  columns: DynamicColumnNode[]
  dateStart: Date
  dateEnd: Date
  detailedDate: boolean
}

const ActivitiesWeekTableRowWrapper: React.FC<
  ActivitiesWeekTableRowWrapperProps
> = ({ dayReport, userId, columns, dateStart, dateEnd, detailedDate }) => {
  const navigate = useNavigate()

  const [open, setOpen] = useState(false)
  const date = parseISO(dayReport.date)
  const dateUrl = format(date, 'yyyy-MM-dd')
  const redirectUrl = dayReport.absence
    ? `/absences?userId=${userId}&date=${dateUrl}`
    : `/activities/${userId}/${dateUrl}`

  return (
    <>
      <ActivitiesWeekTableRow
        key={dateUrl}
        day={dayReport}
        userId={userId}
        dateStart={dateStart}
        dateEnd={dateEnd}
        columns={columns}
        detailedDate={detailedDate}
        onClick={() => void navigate(redirectUrl)}
        onOpen={() => setOpen(!open)}
        open={open}
      />

      {dayReport.groupedActivities.length > 1 &&
        dayReport.groupedActivities.map((_, index) => (
          <ActivitiesWeekTableSubRow
            key={`${dateUrl}-${index}`}
            open={isMobile ? false : open}
            index={index}
            day={dayReport}
            userId={userId}
            dateStart={dateStart}
            dateEnd={dateEnd}
            columns={columns}
            detailedDate={detailedDate}
            onClick={() => void navigate(redirectUrl)}
            onOpen={() => setOpen(!open)}
          />
        ))}
    </>
  )
}
